// v2026-07-28a — PWA Scan barista : factures + mode Traçabilité HACCP (photo aliment → Drive + type/DLC lus par Claude)
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { S, R, TYPO, FS, SHADCN } from './theme';
import {
  Camera, Image as ImageIcon, CheckCircle2, AlertCircle,
  Loader2, X, LogOut, History, FileText,
} from 'lucide-react';

const PWD_KEY = 'sonore_pwd';
const NAME_KEY = 'sonore_uploader';
const MAX_DIM = 2000;
const JPEG_QUALITY = 0.85;

// ── helpers ──────────────────────────────────────────────────────────────────
const eur = (n) => (n == null ? '—' : new Intl.NumberFormat('fr-FR', {
  style: 'currency', currency: 'EUR', maximumFractionDigits: 2,
}).format(n));

const todayISO = () => new Date().toISOString().slice(0, 10);

// Libellés des types d'aliment (traçabilité HACCP)
const TRACA_LABELS = {
  patisserie: 'Pâtisserie', viennoiserie: 'Viennoiserie', sandwich: 'Sandwich',
  produit_laitier: 'Produit laitier', boisson: 'Boisson', epicerie: 'Épicerie',
  fruit_legume: 'Fruits & légumes', surgele: 'Surgelé', autre: 'Autre',
};

// Compression image client : max 2000 px, JPEG 0.85 — réduit ~10 Mo → ~400 Ko
export function compressImage(file) {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith('image/')) {
      // PDF ou autre — on envoie tel quel
      const reader = new FileReader();
      reader.onload = () => resolve({
        dataUrl: reader.result,
        mimeType: file.type || 'application/octet-stream',
        fileName: file.name,
      });
      reader.onerror = reject;
      reader.readAsDataURL(file);
      return;
    }
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const ratio = Math.min(1, MAX_DIM / Math.max(img.width, img.height));
      const w = Math.round(img.width  * ratio);
      const h = Math.round(img.height * ratio);
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, w, h);
      const dataUrl = canvas.toDataURL('image/jpeg', JPEG_QUALITY);
      const baseName = (file.name || 'scan').replace(/\.[^.]+$/, '');
      resolve({
        dataUrl,
        mimeType: 'image/jpeg',
        fileName: `${baseName}-${Date.now()}.jpg`,
      });
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Image illisible')); };
    img.src = url;
  });
}

// ── Login barista (mdp Café) ────────────────────────────────────────────────
function BaristaLogin({ onAuth }) {
  const [name, setName] = useState(() => localStorage.getItem(NAME_KEY) || '');
  const [pwd, setPwd] = useState('');
  const [error, setError] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const cleanName = name.trim();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!pwd || !cleanName || submitting) return;
    setSubmitting(true);
    setError(false);
    // On valide via un ping upload-invoice (HEAD/OPTIONS → 200, POST sans body → 401 si pwd faux)
    try {
      const r = await fetch('/api/upload-invoice', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${pwd}` },
        body:    JSON.stringify({}), // 400 si pwd OK, 401 sinon
      });
      if (r.status === 401) { setError(true); setSubmitting(false); return; }
      // 400 (bad body) = pwd valide ; 201/200 aussi
      localStorage.setItem(PWD_KEY, pwd);
      sessionStorage.setItem(PWD_KEY, pwd);
      localStorage.setItem(NAME_KEY, cleanName);
      onAuth();
    } catch {
      setError(true);
      setSubmitting(false);
    }
  };

  return (
    <div style={{
      minHeight: '100dvh', background: SHADCN.bg, color: SHADCN.fg,
      fontFamily: SHADCN.font, display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', padding: S[5],
    }}>
      <img src="/LOGO.png" alt="Sonore" style={{ height: 28, marginBottom: S[6] }} />
      <form onSubmit={handleSubmit} style={{
        width: '100%', maxWidth: 320, display: 'flex', flexDirection: 'column', gap: S[3],
      }}>
        <div style={{ ...TYPO.eyebrow, color: SHADCN.mutedFg, textAlign: 'center' }}>
          Scan factures — Café Sonore
        </div>
        <input
          type="text"
          inputMode="text"
          autoFocus={!name}
          autoCapitalize="words"
          autoComplete="given-name"
          value={name}
          onChange={(e) => { setName(e.target.value); setError(false); }}
          placeholder="Ton prénom"
          style={{
            padding: `${S[3]}px ${S[4]}px`, fontSize: FS.md, borderRadius: R.md,
            background: SHADCN.cardSolid, color: SHADCN.fg, border: `1px solid ${SHADCN.border}`,
            outline: 'none', WebkitAppearance: 'none',
          }}
        />
        <input
          type="password"
          inputMode="text"
          autoFocus={!!name}
          value={pwd}
          onChange={(e) => { setPwd(e.target.value); setError(false); }}
          placeholder="Mot de passe café"
          style={{
            padding: `${S[3]}px ${S[4]}px`, fontSize: FS.md, borderRadius: R.md,
            background: SHADCN.cardSolid, color: SHADCN.fg, border: `1px solid ${error ? SHADCN.error : SHADCN.border}`,
            outline: 'none', WebkitAppearance: 'none',
          }}
        />
        {error && (
          <div style={{ ...TYPO.caption, color: SHADCN.error, textAlign: 'center' }}>
            Mot de passe incorrect
          </div>
        )}
        <button
          type="submit"
          disabled={!pwd || !cleanName || submitting}
          style={{
            padding: `${S[4]}px`, fontSize: FS.md, fontWeight: 600,
            background: SHADCN.fg, color: SHADCN.bg, border: 'none', borderRadius: R.md,
            cursor: 'pointer', opacity: (!pwd || submitting) ? 0.5 : 1,
          }}
        >
          {submitting ? 'Vérification…' : 'Entrer'}
        </button>
      </form>
    </div>
  );
}

// ── Item d'upload (un par fichier scanné) ────────────────────────────────────
function ScanItem({ item, onRemove }) {
  const { status, preview, result, error, fileName } = item;
  const tone = status === 'done'   ? SHADCN.success
             : status === 'error'  ? SHADCN.error
             : SHADCN.mutedFg;
  return (
    <div style={{
      display: 'flex', gap: S[3], padding: S[3], background: SHADCN.cardSolid,
      borderRadius: R.lg, border: `1px solid ${SHADCN.border}`, alignItems: 'flex-start',
    }}>
      <div style={{
        width: 64, height: 64, borderRadius: R.md, background: SHADCN.bg,
        flexShrink: 0, overflow: 'hidden', display: 'flex',
        alignItems: 'center', justifyContent: 'center',
      }}>
        {preview
          ? <img src={preview} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          : <FileText size={24} color={SHADCN.mutedFg} />}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: S[2], marginBottom: S[1],
        }}>
          {status === 'uploading' && <Loader2 size={14} color={tone} style={{ animation: 'spin 1s linear infinite' }} />}
          {status === 'done'      && <CheckCircle2 size={14} color={tone} />}
          {status === 'error'     && <AlertCircle  size={14} color={tone} />}
          <span style={{ ...TYPO.caption, color: tone, fontWeight: 600 }}>
            {status === 'uploading' && 'Envoi…'}
            {status === 'done'      && (result?.duplicate ? 'Doublon' : result?.type === 'autre' ? 'Ignoré' : 'OK')}
            {status === 'error'     && 'Erreur'}
          </span>
        </div>
        {status === 'done' && result?.type === 'tracabilite' && (
          <div style={{ ...TYPO.body, color: SHADCN.fg }}>
            <div style={{ fontWeight: 600 }}>{result.produit || 'Aliment non identifié'}</div>
            <div style={{ ...TYPO.caption, color: SHADCN.mutedFg }}>
              {TRACA_LABELS[result.type_aliment] || result.type_aliment}
              {result.dlc ? ` · DLC ${result.dlc}` : ''}
            </div>
            {result.warning && (
              <div style={{ ...TYPO.caption, color: SHADCN.warning || SHADCN.error, marginTop: S[1] }}>
                {result.warning}
              </div>
            )}
          </div>
        )}
        {status === 'done' && result?.type !== 'autre' && result?.type !== 'tracabilite' && (
          <div style={{ ...TYPO.body, color: SHADCN.fg }}>
            <div style={{ fontWeight: 600 }}>{result.fournisseur || 'Fournisseur inconnu'}</div>
            <div style={{ ...TYPO.caption, color: SHADCN.mutedFg }}>
              {eur(result.montant_ttc)} · {result.date || '—'}
            </div>
          </div>
        )}
        {status === 'done' && result?.type === 'autre' && (
          <div style={{ ...TYPO.caption, color: SHADCN.mutedFg }}>
            {result.raison || 'Document non reconnu comme facture'}
          </div>
        )}
        {status === 'error' && (
          <div style={{ ...TYPO.caption, color: SHADCN.mutedFg }}>{error || 'Échec upload'}</div>
        )}
        {status === 'uploading' && (
          <div style={{ ...TYPO.caption, color: SHADCN.mutedFg }}>{fileName}</div>
        )}
      </div>
      {status !== 'uploading' && (
        <button onClick={onRemove} aria-label="Retirer" style={{
          background: 'none', border: 'none', color: SHADCN.mutedFg,
          cursor: 'pointer', padding: S[1],
        }}>
          <X size={16} />
        </button>
      )}
    </div>
  );
}

// ── App scan (après login) ──────────────────────────────────────────────────
function ScanApp({ onLogout }) {
  const [items, setItems] = useState([]); // { id, status, preview, fileName, result, error }
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  // mode : 'facture' (scan comptable) | 'traca' (photo traçabilité HACCP)
  const [mode, setMode] = useState('facture');
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const today = todayISO();
      const mois  = today.slice(0, 7);
      if (mode === 'traca') {
        const r = await fetch(`/api/supabase-data?resource=tracabilite&mois=${mois}`, { credentials: 'same-origin' });
        if (r.ok) {
          const j = await r.json();
          setHistory((j?.releves || []).filter(t => t.date === today).slice(0, 50));
        }
      } else {
        const r = await fetch(`/api/factures?mois=${mois}`, {
          credentials: 'same-origin',
        });
        if (r.ok) {
          const j = await r.json();
          const list = Array.isArray(j?.factures) ? j.factures : Array.isArray(j) ? j : [];
          setHistory(list.filter(f => (f.date || '').slice(0, 10) === today).slice(0, 50));
        }
      }
    } catch {}
    setHistoryLoading(false);
  }, [mode]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const upload = useCallback(async (id, payload) => {
    const pwd = localStorage.getItem(PWD_KEY) || sessionStorage.getItem(PWD_KEY) || '';
    const uploadedBy = (localStorage.getItem(NAME_KEY) || '').trim() || null;
    try {
      const r = await fetch('/api/upload-invoice', {
        method:      'POST',
        credentials: 'same-origin',
        headers:     { 'Content-Type': 'application/json', 'Authorization': `Bearer ${pwd}` },
        body: JSON.stringify({
          fileName:      payload.fileName,
          mimeType:      payload.mimeType,
          data:          payload.dataUrl.split(',')[1],
          docType:       mode === 'traca' ? 'tracabilite' : undefined,
          forwardToDext: mode !== 'traca',
          uploadedBy,
        }),
      });
      if (r.status === 401) {
        localStorage.removeItem(PWD_KEY);
        sessionStorage.removeItem(PWD_KEY);
        onLogout();
        return;
      }
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j?.error || `HTTP ${r.status}`);
      setItems(prev => prev.map(it => it.id === id ? { ...it, status: 'done', result: j } : it));
      loadHistory();
    } catch (e) {
      setItems(prev => prev.map(it => it.id === id ? { ...it, status: 'error', error: e.message } : it));
    }
  }, [mode, loadHistory, onLogout]);

  const handleFiles = useCallback(async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    for (const file of files) {
      const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      // Placeholder en "uploading" tout de suite (avant compression)
      setItems(prev => [
        { id, status: 'uploading', preview: null, fileName: file.name, result: null, error: null },
        ...prev,
      ]);
      try {
        const payload = await compressImage(file);
        setItems(prev => prev.map(it => it.id === id ? { ...it, preview: payload.dataUrl } : it));
        await upload(id, payload);
      } catch (e) {
        setItems(prev => prev.map(it => it.id === id ? { ...it, status: 'error', error: e.message } : it));
      }
    }
  }, [upload]);

  return (
    <div style={{
      minHeight: '100dvh', background: SHADCN.bg, color: SHADCN.fg,
      fontFamily: SHADCN.font, paddingBottom: 'calc(env(safe-area-inset-bottom) + 120px)',
    }}>
      {/* Header */}
      <header style={{
        position: 'sticky', top: 0, zIndex: 10,
        background: SHADCN.bg, borderBottom: `1px solid ${SHADCN.border}`,
        padding: `${S[3]}px ${S[4]}px`,
        paddingTop: `calc(env(safe-area-inset-top) + ${S[3]}px)`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <img src="/LOGO.png" alt="Sonore" style={{ height: 22 }} />
        <a href="/" aria-label="Retour dashboard" style={{
          color: SHADCN.mutedFg,
          display: 'flex', alignItems: 'center', gap: S[1],
          padding: S[2], borderRadius: R.md, textDecoration: 'none',
        }}>
          <LogOut size={16} />
          <span style={{ ...TYPO.caption }}>Dashboard</span>
        </a>
      </header>

      <main style={{ padding: S[4], display: 'flex', flexDirection: 'column', gap: S[5] }}>
        {/* Mode : scan facture ou photo traçabilité */}
        <div style={{
          display: 'flex', gap: 4, padding: 4, background: SHADCN.cardSolid,
          borderRadius: R.lg, border: `1px solid ${SHADCN.border}`,
        }}>
          {[
            { id: 'facture', label: 'Factures' },
            { id: 'traca',   label: 'Traçabilité' },
          ].map(m => (
            <button key={m.id} onClick={() => setMode(m.id)} style={{
              flex: 1, padding: `${S[2]}px`, fontSize: FS.body, fontWeight: 600,
              background: mode === m.id ? SHADCN.fg : 'transparent',
              color: mode === m.id ? SHADCN.bg : SHADCN.mutedFg,
              border: 'none', borderRadius: R.md, cursor: 'pointer',
            }}>
              {m.label}
            </button>
          ))}
        </div>
        {mode === 'traca' && (
          <div style={{ ...TYPO.caption, color: SHADCN.mutedFg, marginTop: -S[3] }}>
            Photographie l'aliment avec son étiquette (DLC visible si possible) — le type et la date sont enregistrés automatiquement.
          </div>
        )}

        {/* Items en cours / récents */}
        {items.length > 0 && (
          <section style={{ display: 'flex', flexDirection: 'column', gap: S[2] }}>
            <div style={{ ...TYPO.eyebrow, color: SHADCN.mutedFg }}>Cette session</div>
            {items.map(it => (
              <ScanItem
                key={it.id}
                item={it}
                onRemove={() => setItems(prev => prev.filter(x => x.id !== it.id))}
              />
            ))}
          </section>
        )}

        {/* Historique du jour */}
        <section style={{ display: 'flex', flexDirection: 'column', gap: S[2] }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: S[2], color: SHADCN.mutedFg }}>
            <History size={14} />
            <div style={{ ...TYPO.eyebrow }}>Aujourd'hui</div>
          </div>
          {historyLoading ? (
            <div style={{ ...TYPO.caption, color: SHADCN.mutedFg }}>Chargement…</div>
          ) : history.length === 0 ? (
            <div style={{ ...TYPO.caption, color: SHADCN.mutedFg }}>
              {mode === 'traca' ? "Aucun relevé de traçabilité aujourd'hui." : "Aucune facture scannée aujourd'hui."}
            </div>
          ) : mode === 'traca' ? (
            history.map(t => (
              <div key={t.id} style={{
                padding: S[3], background: SHADCN.cardSolid, borderRadius: R.md,
                border: `1px solid ${SHADCN.border}`,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ ...TYPO.body, fontWeight: 600, color: SHADCN.fg, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {t.produit || 'Aliment'}
                  </div>
                  <div style={{ ...TYPO.caption, color: SHADCN.mutedFg }}>
                    {TRACA_LABELS[t.type_aliment] || t.type_aliment || '—'}
                    {t.auteur ? ` · ${t.auteur}` : ''}
                  </div>
                </div>
                <div style={{ ...TYPO.caption, fontWeight: 600, color: SHADCN.mutedFg, marginLeft: S[3], flexShrink: 0 }}>
                  {t.dlc ? `DLC ${t.dlc.slice(8, 10)}/${t.dlc.slice(5, 7)}` : '—'}
                </div>
              </div>
            ))
          ) : (
            history.map(f => (
              <div key={f.id} style={{
                padding: S[3], background: SHADCN.cardSolid, borderRadius: R.md,
                border: `1px solid ${SHADCN.border}`,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ ...TYPO.body, fontWeight: 600, color: SHADCN.fg, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {f.fournisseur || f.libelle || 'Sans nom'}
                  </div>
                  <div style={{ ...TYPO.caption, color: SHADCN.mutedFg }}>
                    {f.categorie || '—'}
                  </div>
                </div>
                <div style={{ ...TYPO.body, fontWeight: 600, color: SHADCN.fg, marginLeft: S[3] }}>
                  {eur(Math.abs(f.montant || 0))}
                </div>
              </div>
            ))
          )}
        </section>
      </main>

      {/* Barre d'action fixée bas */}
      <div style={{
        position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 10,
        background: SHADCN.bg, borderTop: `1px solid ${SHADCN.border}`,
        padding: `${S[3]}px ${S[4]}px`,
        paddingBottom: `calc(env(safe-area-inset-bottom) + ${S[3]}px)`,
        display: 'flex', gap: S[2],
      }}>
        <button
          onClick={() => cameraRef.current?.click()}
          style={{
            flex: 2, padding: `${S[4]}px`, background: SHADCN.fg, color: SHADCN.bg,
            border: 'none', borderRadius: R.lg, fontSize: FS.md, fontWeight: 600,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: S[2],
            cursor: 'pointer',
          }}>
          <Camera size={20} /> Scanner
        </button>
        <button
          onClick={() => galleryRef.current?.click()}
          aria-label="Importer depuis la galerie"
          style={{
            flex: 1, padding: `${S[4]}px`, background: SHADCN.cardSolid, color: SHADCN.fg,
            border: `1px solid ${SHADCN.border}`, borderRadius: R.lg,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer',
          }}>
          <ImageIcon size={20} />
        </button>
      </div>

      {/* Inputs fichiers cachés */}
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={(e) => { handleFiles(e.target.files); e.target.value = ''; }}
        style={{ display: 'none' }}
      />
      <input
        ref={galleryRef}
        type="file"
        accept="image/*,application/pdf"
        multiple
        onChange={(e) => { handleFiles(e.target.files); e.target.value = ''; }}
        style={{ display: 'none' }}
      />

      <style>{`
        @keyframes spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }
        * { -webkit-tap-highlight-color: transparent; }
        button:active { transform: scale(0.98); transition: transform 0.1s; }
      `}</style>
    </div>
  );
}

// ── Manifest PWA dédié (icône scan + theme noir) ─────────────────────────────
function useScanManifest() {
  useEffect(() => {
    const setOrCreate = (selector, attrs) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement(selector.split('[')[0]);
        document.head.appendChild(el);
      }
      Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
      return el;
    };
    setOrCreate('link[rel="manifest"]',           { rel: 'manifest',           href: '/manifest-scan.json' });
    setOrCreate('link[rel="apple-touch-icon"]',   { rel: 'apple-touch-icon',   href: '/scan-apple-touch.png' });
    setOrCreate('meta[name="theme-color"]',       { name: 'theme-color',       content: '#000000' });
    setOrCreate('meta[name="apple-mobile-web-app-title"]', { name: 'apple-mobile-web-app-title', content: 'Scan' });
    setOrCreate('meta[name="apple-mobile-web-app-capable"]', { name: 'apple-mobile-web-app-capable', content: 'yes' });
    setOrCreate('meta[name="apple-mobile-web-app-status-bar-style"]', { name: 'apple-mobile-web-app-status-bar-style', content: 'black-translucent' });
    document.title = 'Scan Sonore';
  }, []);
}

// ── Root ─────────────────────────────────────────────────────────────────────
export default function Scan() {
  useScanManifest();
  // 3 états : null = check session, false = pas auth, true = auth (cookie OU pwd)
  const [authed, setAuthed] = useState(() =>
    (localStorage.getItem(PWD_KEY) || sessionStorage.getItem(PWD_KEY)) ? true : null
  );

  // Si pas de pwd local, on tente la session cookie du dashboard (membre/admin loggé)
  useEffect(() => {
    if (authed !== null) return;
    let cancelled = false;
    fetch('/api/me', { credentials: 'same-origin' })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (!cancelled) setAuthed(!!data); })
      .catch(() => { if (!cancelled) setAuthed(false); });
    return () => { cancelled = true; };
  }, [authed]);

  if (authed === null) return null; // brief flash pendant le check
  if (!authed) return <BaristaLogin onAuth={() => setAuthed(true)} />;
  return <ScanApp onLogout={() => {
    localStorage.removeItem(PWD_KEY);
    sessionStorage.removeItem(PWD_KEY);
    setAuthed(false);
  }} />;
}
